import ReactDOM from 'react-dom/client'
import { ChakraProvider } from '@chakra-ui/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App.tsx'
import { theme } from './utils/theme.ts'
import { handlePreloadError } from './preload-error-reload.ts'

// Register global debug functions for project overrides (available in browser console)
import './utils/project-overrides.ts'

// Recover from stale-chunk dynamic-import failures after a deploy: a browser
// holding the previous shell can request hashed chunks the new build deleted,
// which fails as "Failed to fetch dynamically imported module" and blanks the
// route. Vite emits `vite:preloadError` for this; reload once (guarded against
// looping) to fetch the fresh shell. See preload-error-reload.ts.
window.addEventListener('vite:preloadError', event => {
  event.preventDefault()
  handlePreloadError({
    storage: window.localStorage,
    now: Date.now(),
    reload: () => window.location.reload(),
  })
})

// Create QueryClient lazily to reduce initial execution time
// This defers React Query initialization until it's actually needed
let queryClientInstance: QueryClient | null = null

const getQueryClient = (): QueryClient => {
  if (!queryClientInstance) {
    queryClientInstance = new QueryClient({
      defaultOptions: {
        queries: {
          // Reduce refetching overhead
          refetchOnWindowFocus: false,
          refetchOnMount: false,
          refetchOnReconnect: false,
          // Reduce stale time to avoid unnecessary re-renders
          staleTime: 60 * 1000, // 1 minute
        },
      },
    })
  }
  return queryClientInstance
}

// Defer icon loading to after initial render using requestIdleCallback
// This prevents icons from blocking the critical rendering path
if ('requestIdleCallback' in window) {
  requestIdleCallback(() => {
    import('./utils/iconLoader.ts').then(module => {
      module.loadIconsCritical().catch(console.error)
    })
  })
} else {
  // Fallback for browsers without requestIdleCallback
  setTimeout(() => {
    import('./utils/iconLoader.ts').then(module => {
      module.loadIconsCritical().catch(console.error)
    })
  }, 1000)
}

const rootElement = document.getElementById('root')

if (!rootElement) {
  throw new Error('Root element not found')
}

// Race condition: Hide loader when app finishes rendering OR after minimum display time
// This ensures loader doesn't unnecessarily cover content, but also avoids flicker
const hideLoader = () => {
  const loader = document.getElementById('app-loader')
  if (!loader) {
    return
  }

  // Mark as hidden and remove after transition
  loader.classList.add('app-loader--hidden')
  window.setTimeout(() => {
    loader.remove()
  }, 600)

  // Mark root as ready so content can be shown
  const root = document.getElementById('root')
  if (root) {
    root.classList.add('ready')
  }
}

// Check if React has rendered content to the DOM
const checkContentReady = (): Promise<void> => {
  return new Promise(resolve => {
    // Use MutationObserver to detect when React has rendered content
    const observer = new MutationObserver(() => {
      const root = document.getElementById('root')
      if (root && root.children.length > 0) {
        // Check if there's actual content (not just empty divs)
        const hasContent = Array.from(root.children).some(
          child => child.children.length > 0 || child.textContent?.trim()
        )
        if (hasContent) {
          observer.disconnect()
          resolve()
        }
      }
    })

    // Start observing immediately
    const root = document.getElementById('root')
    if (root) {
      observer.observe(root, {
        childList: true,
        subtree: true,
      })
    }

    // Fallback: resolve after a reasonable timeout (2 seconds)
    setTimeout(() => {
      observer.disconnect()
      resolve()
    }, 2000)
  })
}

// Race condition: Hide loader when content is ready OR after minimum time
// Minimum display time: 300ms (prevents flicker on fast connections)
// Maximum wait: 2 seconds (ensures loader hides even if content check fails)
const MIN_DISPLAY_TIME = 300
const MAX_WAIT_TIME = 2000

Promise.race([
  // Option 1: Wait for minimum display time, then check if content is ready
  new Promise<void>(resolve => {
    setTimeout(() => {
      checkContentReady().then(resolve)
    }, MIN_DISPLAY_TIME)
  }),
  // Option 2: Maximum wait time (fallback)
  new Promise<void>(resolve => {
    setTimeout(resolve, MAX_WAIT_TIME)
  }),
]).then(() => {
  // Use requestAnimationFrame to ensure DOM is ready for class changes
  requestAnimationFrame(hideLoader)
})

ReactDOM.createRoot(rootElement).render(
  <QueryClientProvider client={getQueryClient()}>
    <ChakraProvider theme={theme}>
      <App />
    </ChakraProvider>
  </QueryClientProvider>
)

// DON'T preload AppKit - let it load only when user clicks connect button
// This defers the wallet-vendor bundle (277KB wasted) until actually needed
// AppKit will be initialized on-demand in AddressButton when user interacts
